import {
  type QuizQuestionSavedPublic,
  QuizzesService,
  type QuizzesUpsertSavedQuizQuestionData,
} from "@/client"
import { HtmlContent } from "@/components/Common/HtmlContent"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { RichTextEditor } from "@/components/ui/rich-text-editor"
import { toaster } from "@/components/ui/toaster"
import { Tooltip } from "@/components/ui/tooltip"
import { containsLocalImageUrls } from "@/utils/richTextImages"
import { Box, Button, HStack, Text, VStack } from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { FaRegBookmark } from "react-icons/fa"

type SaveNoteQuestion = {
  question_id: string
  question_text: string
}

type QuizQuestionSaveNoteModalProps = {
  quizId: string
  question: SaveNoteQuestion
}

export function QuizQuestionSaveNoteModal({
  quizId,
  question,
}: QuizQuestionSaveNoteModalProps) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [noteHtml, setNoteHtml] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [richTextUploadCount, setRichTextUploadCount] = useState(0)
  const saveButtonRef = useRef<HTMLButtonElement>(null)

  const savedQuery = useQuery({
    queryKey: ["quiz-questions-saved", quizId],
    staleTime: 30_000,
    retry: false,
    queryFn: () =>
      QuizzesService.listSavedQuizQuestionStates({ quizId }) as Promise<
        Array<QuizQuestionSavedPublic>
      >,
    select: (savedStates) =>
      savedStates.find(
        (savedState) => savedState.question_id === question.question_id,
      ) ?? null,
  })

  useEffect(() => {
    if (!open || savedQuery.isLoading || savedQuery.isError) return

    if (savedQuery.data) {
      setNoteHtml(savedQuery.data.note_html ?? "")
    } else {
      setNoteHtml("")
    }
  }, [open, savedQuery.data, savedQuery.isError, savedQuery.isLoading])

  const invalidateSavedQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["quiz-questions-saved", quizId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["quiz-question-saved-list"],
      }),
    ])
  }

  const upsertMutation = useMutation({
    mutationFn: (
      requestBody: QuizzesUpsertSavedQuizQuestionData["requestBody"],
    ) =>
      QuizzesService.upsertSavedQuizQuestion({
        quizId,
        questionId: question.question_id,
        requestBody,
      }),
    onSuccess: async () => {
      await invalidateSavedQueries()
      toaster.create({ title: "Note saved", type: "success" })
      setOpen(false)
    },
    onError: () => setErrorMessage("Failed to save note."),
  })

  const deleteMutation = useMutation({
    mutationFn: () =>
      QuizzesService.deleteSavedQuizQuestion({
        quizId,
        questionId: question.question_id,
      }),
    onSuccess: async () => {
      await invalidateSavedQueries()
      setOpen(false)
    },
    onError: () => setErrorMessage("Failed to remove saved question."),
  })

  const isUploadingImage = richTextUploadCount > 0
  const hasSavedState = Boolean(savedQuery.data)
  const hasExistingNote = Boolean(savedQuery.data?.note_html)
  const hasLookupError = savedQuery.isError
  const isBusy =
    savedQuery.isLoading || upsertMutation.isPending || deleteMutation.isPending

  const handleSave = () => {
    if (hasLookupError) return
    if (isUploadingImage || containsLocalImageUrls(noteHtml)) {
      setErrorMessage("Wait for images to finish uploading before saving.")
      return
    }
    setErrorMessage("")
    upsertMutation.mutate({
      is_bookmarked: true,
      note_html: noteHtml.trim() ? noteHtml : null,
    })
  }

  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const tag = target?.tagName?.toLowerCase()
      const isTypingTarget =
        target?.isContentEditable ||
        tag === "input" ||
        tag === "textarea" ||
        tag === "select"

      if (
        isTypingTarget ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.key.toLowerCase() !== "s"
      ) {
        return
      }

      event.preventDefault()
      saveButtonRef.current?.click()
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [open])

  return (
    <DialogRoot
      lazyMount
      size={{ base: "sm", md: "xl" }}
      placement="center"
      open={open}
      onOpenChange={({ open: nextOpen }) => {
        setOpen(nextOpen)
        if (!nextOpen) setErrorMessage("")
      }}
    >
      <Tooltip content="Bookmark this question or add a note">
        <DialogTrigger asChild>
          <Button
            aria-label={hasExistingNote ? "Edit note" : "Save note"}
            size="sm"
            variant="ghost"
            gap={{ base: 0, md: 2 }}
            px={{ base: 2, md: 4 }}
          >
            <FaRegBookmark />
            <Box as="span" display={{ base: "none", md: "inline" }}>
              {hasExistingNote ? "Edit Note" : "Save note"}
            </Box>
          </Button>
        </DialogTrigger>
      </Tooltip>

      <DialogContent maxW="2xl">
        <DialogHeader>
          <DialogTitle>Save quiz question note</DialogTitle>
        </DialogHeader>
        <DialogCloseTrigger />
        <DialogBody pb={6}>
          <VStack align="stretch" gap={4}>
            <Box
              borderWidth={1}
              borderColor="border"
              borderRadius="lg"
              p={4}
              bg="bg.subtle"
            >
              <Text fontSize="xs" fontWeight="bold" mb={2} color="fg.muted">
                Question
              </Text>
              <HtmlContent content={question.question_text} />
            </Box>

            <Checkbox checked disabled>
              Bookmark this question
            </Checkbox>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Note
              </Text>
              <RichTextEditor
                value={noteHtml}
                onChange={setNoteHtml}
                onUploadStateChange={(isUploading) => {
                  setRichTextUploadCount((count) =>
                    Math.max(0, count + (isUploading ? 1 : -1)),
                  )
                }}
                onUploadError={setErrorMessage}
                placeholder="Add your own study note"
                minHeight="180px"
              />
            </Box>

            {errorMessage ? (
              <Text color="red.500" fontSize="sm">
                {errorMessage}
              </Text>
            ) : null}
            {hasLookupError ? (
              <Text color="red.500" fontSize="sm">
                Failed to load the saved note. Close and reopen to try again.
              </Text>
            ) : null}
          </VStack>
        </DialogBody>
        <DialogFooter>
          <HStack justify="space-between" w="full">
            <Button
              variant="ghost"
              colorPalette="red"
              onClick={() => deleteMutation.mutate()}
              disabled={!hasSavedState || isBusy || hasLookupError}
            >
              Remove saved question
            </Button>
            <Button
              ref={saveButtonRef}
              onClick={handleSave}
              loading={isBusy}
              disabled={isBusy || hasLookupError}
            >
              Save
            </Button>
          </HStack>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
